import { isNil, sortBy } from 'lodash-es'
import { UOMContract } from 'models'
import { useCallback } from 'react'

const useUOMConversion = (uomList: UOMContract[]) => {
    const getChildrenUOMs = useCallback(
        (uom: UOMContract, filteredUOMs: UOMContract[]) => {
            var childrenUOMs = filteredUOMs.filter(
                (u) => u.referenceUOMID && u.referenceUOMID === uom.uomid,
            )

            childrenUOMs = [
                ...childrenUOMs,
                ...childrenUOMs
                    .map((uom) => getChildrenUOMs(uom, filteredUOMs))
                    .flat(),
            ]

            return childrenUOMs
        },
        [],
    )

    const getParentWithChidrenUOMs = useCallback(
        (uom: UOMContract, filteredUOMs: UOMContract[]) => {
            var foundUOMs = [uom]
            var childrenUOMs = getChildrenUOMs(uom, filteredUOMs)
            foundUOMs = foundUOMs.concat(childrenUOMs)

            if (uom.referenceUOMID) {
                var refUOM = filteredUOMs.find(
                    (u) => u.uomid === uom.referenceUOMID,
                )
                if (refUOM) {
                    var unvisitedUOMs = filteredUOMs.filter(
                        (u) =>
                            u.uomid !== uom.uomid &&
                            !foundUOMs.some((c) => c.uomid === u.uomid),
                    )
                    var parentUOMs = getParentWithChidrenUOMs(
                        refUOM,
                        unvisitedUOMs,
                    )
                    foundUOMs = foundUOMs.concat(parentUOMs)
                }
            }
            return foundUOMs
        },
        [getChildrenUOMs],
    )

    const getConvertibleUOMs = useCallback(
        (uomID: number) => {
            const currentUOM = uomList.find((i) => i.uomid === uomID)
            if (isNil(currentUOM)) return []

            if (currentUOM.enableConversion) {
                const uoms = getParentWithChidrenUOMs(
                    currentUOM,
                    uomList.filter(
                        (u) =>
                            u.uomClassID === currentUOM.uomClassID &&
                            u.enableConversion,
                    ),
                )
                return sortBy(uoms, (i) => i.uomid)
            }
            // Disable Conversion UOMs : Can use all disable Conversion uoms within the same class
            return uomList.filter(
                (u) =>
                    u.uomClassID === currentUOM.uomClassID &&
                    !u.enableConversion,
            )
        },
        [getParentWithChidrenUOMs, uomList],
    )

    const onConvertUOM = useCallback(
        (
            quantity: number,
            sourceUOMId: number,
            targetUOMId: number,
            convertibleUOMs: UOMContract[],
        ): number => {
            const sourceUOM = convertibleUOMs.find(
                (c) => c.uomid === sourceUOMId,
            )
            const targetUOM = convertibleUOMs.find(
                (c) => c.uomid === targetUOMId,
            )

            // if there are the same UOMs or they both disable conversion aka they are independent UOMs
            // -> do not convert, just return quantity
            if (isNil(sourceUOM) || isNil(targetUOM)) return quantity

            if (
                sourceUOMId === targetUOMId ||
                (!sourceUOM.enableConversion && !targetUOM.enableConversion)
            )
                return quantity

            // target uom is the ref UOM of source uom -> qt * source's conversion
            // ex : source (g) / target (kg) : g ref kg => g's conversion = 0.001 (1 g = 0.001 kg)
            // - 50 g -> return 50 * 0.001 = 0.05 kg
            if (
                sourceUOM.referenceUOMID &&
                sourceUOM.referenceUOMID === targetUOM.uomid
            )
                return quantity * (sourceUOM.conversion ?? 0)

            // source uom is the ref UOM of target uom -> qt * target's factor
            // ex : source (kg) / target (g) : g ref kg => g's factor = 1000 (1000 g = 1 kg)
            // - 5 kg -> return 5 * 1000 = 5000 g
            if (
                targetUOM.referenceUOMID &&
                sourceUOM.uomid === targetUOM.referenceUOMID
            )
                return quantity * (targetUOM.factor ?? 0)

            // target uom has no ref uom (the peak to the tree)
            // Assume all uoms within the same tree must can convert to the base UOM
            if (targetUOM.isBaseUOM && sourceUOM.referenceUOMID) {
                // convert source uom 1 upper UOM (ref uom)
                // ex :
                // kg (target) is the base uom
                // - g ref to kg (convert : 0.001 / factor : 1000)
                // - mg (source) ref to g (convert : 0.001 / factor : 1000)
                // 50 mg -> 50 * 0.001 = 0.05 g (change g to be source instead of mg)
                // 0.05 g -> 0.05 * 0.001 = 0.00005 kg
                const convertedQuantity = quantity * (sourceUOM.conversion ?? 0)
                return onConvertUOM(
                    convertedQuantity,
                    sourceUOM.referenceUOMID,
                    targetUOMId,
                    convertibleUOMs,
                )
            }

            // source uom has no ref uom (the peak to the tree)
            // find new factor from target UOM to source UOM
            // ex :
            // kg (source) is the base uom
            // - g ref to kg (convert : 0.001 / factor : 1000)
            // - mg (target) ref to g (convert : 0.001 / factor : 1000)
            if (sourceUOM.isBaseUOM && targetUOM.referenceUOMID) {
                // find 1 target UOM = x source UOM
                // ex : 1 mg -> 0.001 g -> 0.000001 kg aka 10^-6
                const newFactor: number = onConvertUOM(
                    1,
                    targetUOMId,
                    sourceUOMId,
                    convertibleUOMs,
                )
                // convert 20 kg to mg : 20 / 10^-6 = 20 * 10^6 = 2 * 10^7 mg
                return quantity / newFactor
            }
            // Out of above cases, assume that Both UOMs are not the Base UOM
            // Check if ref to the same uom
            // source uom and target uom have the same ref uom -> qt * source's conversion * target's factor
            // ex : source (mg) / target (g) :
            // mg ref kg => mg's conversion = 0.000001 (1 mg = 0.000001 kg) g ref kg => g's factor = 1000 (1000 g = 1 kg)
            // - 600 mg -> return 600 * 0.000001 * 1000 = 0.6 g
            if (sourceUOM.referenceUOMID === targetUOM.referenceUOMID)
                return (
                    quantity *
                    (sourceUOM.conversion ?? 0) *
                    (targetUOM.factor ?? 0)
                )

            // convert to base UOM : Assume each uom tree have a base uom
            // ex : source (mg) / target (g) :
            // mg ref kg => mg's conversion = 0.000001 (1 mg = 0.000001 kg) g ref kg => g's factor = 1000 (1000 g = 1 kg)
            // - 600 mg -> return 600 * 0.000001 * 1000 = 0.6 g

            const baseUOM = convertibleUOMs.find((i) => i.isBaseUOM)!
            // quantity from source UOM -> Base UOM
            const sourceToBaseUOMQuantity = onConvertUOM(
                quantity,
                sourceUOMId,
                baseUOM.uomid!,
                convertibleUOMs,
            )
            // conversion from source UOM => target UOM -> base UOM => target UOM
            // 1 base UOM -> x target UOM
            const baseToTargetUOMConversion = onConvertUOM(
                1,
                baseUOM.uomid!,
                targetUOMId,
                convertibleUOMs,
            )

            return sourceToBaseUOMQuantity * baseToTargetUOMConversion
        },
        [],
    )

    const convertUOM = useCallback(
        (
            quantity: number,
            sourceUOMId: number | undefined,
            targetUOMId: number | undefined,
        ): number => {
            if (isNil(sourceUOMId) || isNil(targetUOMId)) return quantity

            const convertibleUOMs = getConvertibleUOMs(sourceUOMId)

            return onConvertUOM(
                quantity,
                sourceUOMId,
                targetUOMId,
                convertibleUOMs,
            )
        },
        [getConvertibleUOMs, onConvertUOM],
    )

    return {
        convertUOM,
        getConvertibleUOMs,
    } as const
}

export default useUOMConversion
